import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress
} from '@mui/material';
import {
  Person,
  DirectionsCar,
  Speed,
  Warning,
  CheckCircle,
  ExpandMore,
  Refresh,
  Visibility,
  TrendingUp,
  TrendingDown,
  Schedule
} from '@mui/icons-material';
import apiClient from '../api';

const DriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [safetyScores, setSafetyScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDetails, setDriverDetails] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadDriverData();
  }, [dateRange]);

  const loadDriverData = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      // Load drivers and safety scores in parallel
      const [driversResponse, safetyResponse] = await Promise.allSettled([
        apiClient.get('/samsara/drivers'),
        apiClient.get(`/samsara/drivers/safety-scores?startTime=${startDate.toISOString()}&endTime=${endDate.toISOString()}`)
      ]);

      if (driversResponse.status === 'fulfilled') {
        // Samsara API might return { data: [...] } or direct array
        const driversData = driversResponse.value.data;
        const driversArray = driversData?.data || (Array.isArray(driversData) ? driversData : []);
        setDrivers(driversArray);
        console.log(`[DriverManagement] Loaded ${driversArray.length} drivers`);
      } else {
        console.warn('Failed to load drivers:', driversResponse.reason);
        setDrivers([]);
      }

      if (safetyResponse.status === 'fulfilled') {
        // Samsara API might return { data: [...] } or direct array
        const safetyData = safetyResponse.value.data;
        const safetyArray = safetyData?.data || (Array.isArray(safetyData) ? safetyData : []);
        setSafetyScores(safetyArray);
      } else {
        console.warn('Failed to load safety scores:', safetyResponse.reason);
        setSafetyScores([]);
      }
    } catch (err) {
      console.error('Error loading driver data:', err);
      setError('Failed to load driver data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDriverData();
    setRefreshing(false);
  };

  const handleViewDriver = async (driver) => {
    setSelectedDriver(driver);
    setDetailsOpen(true);
    
    try {
      // Load driver details and stats
      const [detailsResponse, statsResponse] = await Promise.allSettled([
        apiClient.get(`/samsara/drivers/${driver.id}`),
        apiClient.get(`/samsara/drivers/${driver.id}/stats?startTime=${getDateRange().start}&endTime=${getDateRange().end}`)
      ]);

      if (detailsResponse.status === 'fulfilled') {
        setDriverDetails(detailsResponse.value.data);
      }

      if (statsResponse.status === 'fulfilled') {
        const statsData = statsResponse.value.data;
        // Check if stats are empty (all zeros) - this means the endpoint is not available
        if (statsData && 
            (!statsData.fuelUsed || statsData.fuelUsed.value === 0) &&
            (!statsData.distance || statsData.distance.value === 0) &&
            (!statsData.engineHours || statsData.engineHours.value === 0)) {
          // Stats endpoint not available, but don't set error - just don't show stats
          setDriverStats(null);
        } else {
          setDriverStats(statsData);
        }
      } else {
        // Stats failed to load, but this is optional data
        console.warn('Failed to load driver stats:', statsResponse.reason);
        setDriverStats(null);
      }
    } catch (err) {
      console.error('Error loading driver details:', err);
    }
  };

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  const getDriverSafetyScore = (driverId) => {
    const score = safetyScores.find(s => s.driverId === driverId);
    return score ? score.score : null;
  };

  const getSafetyScoreColor = (score) => {
    if (score === null || score === undefined) return 'default';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'on_duty':
        return 'success';
      case 'off_duty':
        return 'default';
      case 'sleeper':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const sortedDrivers = [...drivers].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'safety':
        return (getDriverSafetyScore(b.id) || 0) - (getDriverSafetyScore(a.id) || 0);
      case 'status':
        return (a.status || '').localeCompare(b.status || '');
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              Driver Management
            </Typography>
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="safety">Safety Score</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh Data">
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Drivers Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Driver</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Safety Score</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDrivers.map((driver) => {
                  const safetyScore = getDriverSafetyScore(driver.id);
                  return (
                    <TableRow key={driver.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {driver.name || driver.username || `Driver ${driver.id}`}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            ID: {driver.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={driver.status || 'Unknown'}
                          color={getStatusColor(driver.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {safetyScore !== null ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={`${safetyScore}%`}
                              color={getSafetyScoreColor(safetyScore)}
                              size="small"
                            />
                            {safetyScore >= 80 ? (
                              <TrendingUp color="success" fontSize="small" />
                            ) : safetyScore < 60 ? (
                              <TrendingDown color="error" fontSize="small" />
                            ) : null}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No data
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {driver.currentVehicle ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <DirectionsCar fontSize="small" />
                            <Typography variant="body2">
                              {driver.currentVehicle.name || driver.currentVehicle.id}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(driver.lastSeen || driver.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDriver(driver)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {drivers.length === 0 && (
            <Box textAlign="center" py={4}>
              <Alert severity="info">
                No drivers found. Configure Samsara integration to view driver data.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Driver Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Driver Details: {selectedDriver?.name || selectedDriver?.username || `Driver ${selectedDriver?.id}`}
            </Typography>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDriver && (
            <Box>
              {/* Driver Information */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Driver Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Driver ID: {selectedDriver.id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Status: 
                        <Chip
                          label={selectedDriver.status || 'Unknown'}
                          color={getStatusColor(selectedDriver.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Username: {selectedDriver.username || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Last Activity: {formatDate(selectedDriver.lastSeen || selectedDriver.updatedAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Safety Score */}
              {getDriverSafetyScore(selectedDriver.id) !== null && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">Safety Performance</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Safety Score</Typography>
                        <Typography variant="h6">
                          {getDriverSafetyScore(selectedDriver.id)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getDriverSafetyScore(selectedDriver.id)}
                        color={getSafetyScoreColor(getDriverSafetyScore(selectedDriver.id))}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Performance Stats */}
              {driverStats && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">Performance Statistics</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box textAlign="center">
                          <Speed color="primary" sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="h6">
                            {formatNumber(driverStats.distance?.value)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {driverStats.distance?.unit || 'miles'} Driven
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box textAlign="center">
                          <Schedule color="primary" sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="h6">
                            {formatNumber(driverStats.driveTime?.value)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {driverStats.driveTime?.unit || 'hours'} Drive Time
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* HOS Compliance */}
              {driverDetails?.hosStatus && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">Hours of Service (HOS)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Status: 
                          <Chip
                            label={driverDetails.hosStatus.status || 'Unknown'}
                            color={driverDetails.hosStatus.status === 'compliant' ? 'success' : 'warning'}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          Hours Remaining: {driverDetails.hosStatus.hoursRemaining || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DriverManagement;
